/**
 * The maps that already exist: find one, open it, rename it, delete it.
 *
 * Filtering happens in the request rather than in the client — scope, subject,
 * and when the map was last worked on — because a therapist with two hundred
 * maps should not be sent two hundred maps to narrow down, and because a
 * patient filter must not be a suggestion.
 *
 * Opened from a patient's own surface the subject is already settled, so the
 * filters that would only repeat it are not drawn.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import flatpickr from "flatpickr";
import { Eye, Loader2, Network, Pencil, Search, Share2, Trash2, User, Users } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../shared/ui/icons/SubjectKindIcon";

import Button from "../../../../shared/ui/button/Button";
import DatePicker from "../../../../shared/ui/form/date-picker";
import FilterSelect from "../../../../shared/ui/form/FilterSelect";
import Label from "../../../../shared/ui/form/Label";
import { useTherapist } from "../../../../context/useTherapist";
import { offeredCaseload, useCourseLabels, useSessionPreferences } from "../../../center";
import { groupLabel, type GroupPatient } from "../../../patient";
import type { CouplePatient, Patient } from "../../../patient";
import {
    listSystemicMaps,
    loadMapSubjectCaseload,
    removeSystemicMap,
    renameSystemicMap,
} from "../../application/systemicMaps";
import {
    MAP_KINDS,
    MAX_TITLE_LENGTH,
    type MapKind,
    type SystemicMapSummary,
} from "../../domain/types";

const PAGE_SIZE = 8;

type Scope = "all" | "individual" | "couple" | "group" | "open";

const SCOPES: { key: Scope; labelKey: string }[] = [
    { key: "all", labelKey: "systemicMap.list.all" },
    { key: "individual", labelKey: "common.individual" },
    { key: "couple", labelKey: "common.couple" },
    { key: "group", labelKey: "common.group" },
    { key: "open", labelKey: "systemicMap.list.openBoard" },
];

/** Which of the four a row is, said in a word rather than inferred. */
const scopeOf = (map: SystemicMapSummary): Exclude<Scope, "all"> =>
    map.group_patient_id
        ? "group"
        : map.couple_patient_id
          ? "couple"
          : map.patient_id
            ? "individual"
            : "open";

const SCOPE_BADGE: Record<Exclude<Scope, "all">, string> = {
    individual: "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300",
    couple: "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    group: "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    open: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

interface Props {
    /** Scopes the list to one patient. Absent on the home surface. */
    patientId?: string;
    couplePatientId?: string;
    groupPatientId?: string;
    /** Re-fetches when this changes — a modal passes its open state. */
    active?: boolean;
    /** Opens a board in a new tab rather than navigating. */
    openInNewTab?: boolean;
    /** Hands the chosen map to the caller instead of going anywhere — a call opens it on its stage. */
    onOpenMap?: (mapId: string) => void;
}

type Status = "loading" | "ready" | "error";

export default function MapList({
    patientId,
    couplePatientId,
    groupPatientId,
    active = true,
    openInNewTab = false,
    onOpenMap,
}: Props) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { therapist } = useTherapist();
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();
    const [maps, setMaps] = useState<SystemicMapSummary[]>([]);
    const [total, setTotal] = useState(0);
    const [status, setStatus] = useState<Status>("loading");
    const [renaming, setRenaming] = useState<SystemicMapSummary | null>(null);
    const [confirming, setConfirming] = useState<SystemicMapSummary | null>(null);
    const [busy, setBusy] = useState(false);

    // The subject is already settled when the list is opened from a patient.
    const fixedSubject = Boolean(patientId || couplePatientId || groupPatientId);
    const [scope, setScope] = useState<Scope>("all");
    const [kind, setKind] = useState<MapKind | "all">("all");
    const [subject, setSubject] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [offset, setOffset] = useState(0);
    const [subjects, setSubjects] = useState<
        { value: string; label: string; icon: React.ReactNode }[]
    >([]);

    // Couples and groups only where the centre runs them.
    const { modes } = useSessionPreferences();

    useEffect(() => {
        if (!active || fixedSubject || !therapist?.id) return;
        loadMapSubjectCaseload(therapist.id)
            .then((caseload) => offeredCaseload(caseload, modes))
            .then(({ patients, couples, groups }) => {
                setSubjects([
                    ...(patients || []).map((patient: Patient) => ({
                        value: `patient:${patient.id}`,
                        label: labelOf(patient),
                        icon: <User className="h-4 w-4" />,
                    })),
                    ...(couples || []).map((couple: CouplePatient) => ({
                        value: `couple:${couple.id}`,
                        label: couple.display_name,
                        icon: <Users className="h-4 w-4" />,
                    })),
                    ...(groups || []).map((group: GroupPatient) => ({
                        value: `group:${group.id}`,
                        label: groupLabel(group),
                        icon: <GroupOfPeopleIcon className="h-4 w-4" />,
                    })),
                ]);
            })
            .catch(() => setSubjects([]));
    }, [active, fixedSubject, therapist?.id, labelOf, modes]);

    // A changed filter is a different question; asking it from page three
    // would answer with the third page of something nobody asked for.
    useEffect(() => {
        setOffset(0);
    }, [scope, kind, subject, fromDate, toDate]);

    const load = useCallback(async () => {
        setStatus("loading");
        // Named apart from the map's own kind: this one says whether the
        // chosen subject is a patient or a couple.
        const [subjectKind, id] = subject.split(":");
        try {
            const data = await listSystemicMaps({
                scope: scope === "all" ? undefined : scope,
                kind: kind === "all" ? undefined : kind,
                patient_id: patientId ?? (subjectKind === "patient" ? id : undefined),
                couple_patient_id:
                    couplePatientId ?? (subjectKind === "couple" ? id : undefined),
                group_patient_id:
                    groupPatientId ?? (subjectKind === "group" ? id : undefined),
                since: fromDate ? new Date(`${fromDate}T00:00:00`).toISOString() : undefined,
                until: toDate ? new Date(`${toDate}T23:59:59`).toISOString() : undefined,
                offset,
                limit: PAGE_SIZE,
            });
            setMaps(data.items);
            setTotal(data.total);
            setStatus("ready");
        } catch {
            setStatus("error");
        }
    }, [patientId, couplePatientId, groupPatientId, scope, kind, subject, fromDate, toDate, offset]);

    useEffect(() => {
        if (active) void load();
    }, [active, load]);

    const page = Math.floor(offset / PAGE_SIZE) + 1;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const dateKey = `${fromDate}-${toDate}`;
    const filtered = scope !== "all" || kind !== "all" || subject || fromDate || toDate;

    const clearFilters = () => {
        setScope("all");
        setKind("all");
        setSubject("");
        setFromDate("");
        setToDate("");
    };

    const remove = async (map: SystemicMapSummary) => {
        setBusy(true);
        try {
            await removeSystemicMap(map.id);
            setConfirming(null);
            // Re-asked rather than spliced: a page of eight with one gone is
            // a page of seven, and the row that should fill it is on the
            // server.
            await load();
        } finally {
            setBusy(false);
        }
    };

    const rename = async (map: SystemicMapSummary, title: string) => {
        setBusy(true);
        try {
            const data = await renameSystemicMap(map.id, title);
            setMaps((current) =>
                current.map((entry) =>
                    entry.id === map.id ? { ...entry, title: data.title } : entry,
                ),
            );
            setRenaming(null);
        } finally {
            setBusy(false);
        }
    };

    const open = (map: SystemicMapSummary) => {
        if (onOpenMap) {
            onOpenMap(map.id);
            return;
        }
        const path = `/systemic-map/${map.id}`;
        if (openInNewTab) {
            window.open(path, "_blank", "noopener,noreferrer");
            return;
        }
        navigate(path);
    };

    const when = (iso: string) =>
        new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
            new Date(iso),
        );

    return (
        <div>
            {!fixedSubject && (
                <div className="mb-4 flex flex-col gap-3">
                    <div className="flex flex-wrap gap-1.5">
                        {SCOPES.map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() => setScope(option.key)}
                                aria-pressed={scope === option.key}
                                className={`min-h-11 rounded-full px-3 text-xs font-semibold transition-colors duration-fast ${
                                    scope === option.key
                                        ? "bg-brand-600 text-white"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
                                }`}
                            >
                                {t(option.labelKey)}
                            </button>
                        ))}
                    </div>

                    {/* The instrument, on its own row: a genogram and an
                        ecomap are different instruments, and which one a
                        therapist wants is a different question from whose it
                        is. */}
                    <div className="flex flex-wrap gap-1.5">
                        {(["all", ...MAP_KINDS] as const).map((option) => (
                            <button
                                key={option}
                                type="button"
                                onClick={() => setKind(option)}
                                aria-pressed={kind === option}
                                className={`min-h-11 rounded-full border px-3 text-xs font-semibold transition-colors duration-fast ${
                                    kind === option
                                        ? "border-brand-600 text-brand-600"
                                        : "border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400"
                                }`}
                            >
                                {t(
                                    option === "all"
                                        ? "systemicMap.list.allKinds"
                                        : `systemicMap.kind.${option}`,
                                )}
                            </button>
                        ))}
                    </div>

                    <FilterSelect
                        icon={<Search className="h-4 w-4" />}
                        options={subjects}
                        value={subject}
                        onChange={(value) => setSubject(typeof value === "string" ? value : "")}
                        placeholder={t("systemicMap.list.subjectFilter")}
                        searchPlaceholder={t("patient.searchPlaceholder", "Search...")}
                        className="w-full bg-gray-50 dark:bg-gray-800/50"
                    />

                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div>
                            <Label htmlFor="map-from-date">
                                {t("systemicMap.list.fromDate")}
                            </Label>
                            <DatePicker
                                key={`from-${dateKey}`}
                                id="map-from-date"
                                label=""
                                placeholder={t("session.selectStartDate")}
                                defaultDate={fromDate}
                                onChange={(dates: Date[]) =>
                                    setFromDate(
                                        dates[0] ? flatpickr.formatDate(dates[0], "Y-m-d") : "",
                                    )
                                }
                            />
                        </div>
                        <div>
                            <Label htmlFor="map-to-date">{t("systemicMap.list.toDate")}</Label>
                            <DatePicker
                                key={`to-${dateKey}`}
                                id="map-to-date"
                                label=""
                                placeholder={t("session.selectEndDate")}
                                defaultDate={toDate}
                                onChange={(dates: Date[]) =>
                                    setToDate(
                                        dates[0] ? flatpickr.formatDate(dates[0], "Y-m-d") : "",
                                    )
                                }
                            />
                        </div>
                    </div>

                    {filtered && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            className="self-start text-xs font-medium text-brand-600 hover:underline"
                        >
                            {t("common.clear", "Clear")}
                        </button>
                    )}
                </div>
            )}

            {status === "loading" && (
                <p
                    role="status"
                    className="flex items-center gap-2 py-8 text-sm text-content-secondary"
                >
                    <Loader2
                        size={16}
                        aria-hidden="true"
                        className="animate-spin motion-reduce:animate-none"
                    />
                    {t("systemicMap.page.loading")}
                </p>
            )}

            {status === "error" && (
                <div className="py-8 text-center">
                    <p role="alert" className="text-sm text-status-critical">
                        {t("systemicMap.list.loadError")}
                    </p>
                    <Button
                        size="sm"
                        variant="outline"
                        className="mt-3"
                        onClick={() => void load()}
                    >
                        {t("systemicMap.list.retry")}
                    </Button>
                </div>
            )}

            {status === "ready" && maps.length === 0 && (
                <div className="py-10 text-center">
                    <p className="text-sm font-medium text-content-primary">
                        {filtered
                            ? t("systemicMap.list.noMatches")
                            : t("systemicMap.list.empty")}
                    </p>
                    <p className="mt-1 text-sm text-content-secondary">
                        {filtered
                            ? t("systemicMap.list.noMatchesHint")
                            : t("systemicMap.list.emptyHint")}
                    </p>
                </div>
            )}

            {status === "ready" && maps.length > 0 && (
                <ul className="divide-y divide-border-subtle">
                    {maps.map((map) => (
                        <li
                            key={map.id}
                            className="flex items-center gap-1 py-1"
                            data-testid={`map-row-${map.id}`}
                        >
                            {/* The row is the way in. A board opens by being
                                pointed at, the way a row in any list does;
                                an "open" button beside it was a second thing
                                to aim at for the same result. */}
                            <button
                                type="button"
                                onClick={() => open(map)}
                                className="flex min-w-0 flex-1 items-center gap-3 rounded-lg py-2 pl-1 pr-2 text-left transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                            <span className="text-content-secondary" aria-hidden="true">
                                {map.kind === "genogram" ? (
                                    <Network size={18} />
                                ) : (
                                    <Share2 size={18} />
                                )}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm font-medium text-content-primary">
                                    {map.title}
                                </span>
                                {map.subject_name && (
                                    // Whose map this is, said outright: a
                                    // therapist picking from a list of
                                    // genograms is looking for a family, not
                                    // for a title.
                                    <span className="block truncate text-xs font-medium text-content-secondary">
                                        {map.subject_name}
                                    </span>
                                )}
                                <span className="block text-xs text-content-secondary">
                                    {map.shared_with_patient_at && (
                                        <>
                                            {/* Which maps the family can read
                                                is worth seeing from the list,
                                                not only from inside one. */}
                                            <span className="inline-flex items-center gap-1 font-medium text-content-primary">
                                                <Eye size={12} aria-hidden="true" />
                                                {t("systemicMap.list.sharedWithPatient")}
                                            </span>{" "}
                                            ·{" "}
                                        </>
                                    )}
                                    {t(`systemicMap.kind.${map.kind}`)} ·{" "}
                                    {t("systemicMap.list.elements", {
                                        count: map.element_count,
                                    })}{" "}
                                    ·{" "}
                                    {t("systemicMap.list.updated", {
                                        when: when(map.updated_at),
                                    })}
                                </span>
                            </span>

                            <span
                                className={`hidden shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold uppercase sm:inline ${
                                    SCOPE_BADGE[scopeOf(map)]
                                }`}
                            >
                                {t(
                                    scopeOf(map) === "open"
                                        ? "systemicMap.list.openBoard"
                                        : `common.${scopeOf(map)}`,
                                )}
                            </span>

                            </button>

                            <IconButton
                                label={t("systemicMap.list.rename")}
                                onClick={() => setRenaming(map)}
                            >
                                <Pencil size={16} aria-hidden="true" />
                            </IconButton>
                            <IconButton
                                label={t("systemicMap.list.delete")}
                                destructive
                                onClick={() => setConfirming(map)}
                            >
                                <Trash2 size={16} aria-hidden="true" />
                            </IconButton>
                        </li>
                    ))}
                </ul>
            )}

            {status === "ready" && total > PAGE_SIZE && (
                <div className="mt-4 flex items-center justify-between text-xs text-content-secondary">
                    <span>{t("systemicMap.list.pageOf", { page, pageCount })}</span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={offset === 0}
                            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                        >
                            {t("common.previous", "Previous")}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={offset + PAGE_SIZE >= total}
                            onClick={() => setOffset(offset + PAGE_SIZE)}
                        >
                            {t("common.next", "Next")}
                        </Button>
                    </div>
                </div>
            )}

            {renaming && (
                <RenameForm
                    map={renaming}
                    busy={busy}
                    onCancel={() => setRenaming(null)}
                    onSubmit={(title) => void rename(renaming, title)}
                />
            )}

            {confirming && (
                <div className="mt-5 rounded-xl border border-status-critical/40 bg-status-critical/5 p-4">
                    <p className="text-sm font-medium text-content-primary">
                        {t("systemicMap.list.deleteTitle")}
                    </p>
                    <p className="mt-1 text-sm text-content-secondary">
                        {t("systemicMap.list.deleteWarning")}
                    </p>
                    <div className="mt-3 flex gap-2">
                        <Button
                            size="sm"
                            variant="destructive"
                            loading={busy}
                            onClick={() => void remove(confirming)}
                        >
                            {t("systemicMap.list.delete")}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                            {t("common.cancel")}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function RenameForm({
    map,
    busy,
    onCancel,
    onSubmit,
}: {
    map: SystemicMapSummary;
    busy: boolean;
    onCancel: () => void;
    onSubmit: (title: string) => void;
}) {
    const { t } = useTranslation();
    const [title, setTitle] = useState(map.title);
    const valid = title.trim().length > 0;

    return (
        <form
            className="mt-5 rounded-xl border border-border-subtle p-4"
            onSubmit={(event) => {
                event.preventDefault();
                if (valid) onSubmit(title.trim());
            }}
        >
            <label className="block">
                <span className="mb-1 block text-[13px] font-medium text-content-secondary">
                    {t("systemicMap.list.renameTitle")}
                </span>
                <input
                    type="text"
                    value={title}
                    autoFocus
                    maxLength={MAX_TITLE_LENGTH}
                    onChange={(event) => setTitle(event.target.value)}
                    className="min-h-11 w-full rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm text-content-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                />
            </label>
            <div className="mt-3 flex gap-2">
                <Button size="sm" type="submit" loading={busy} disabled={!valid}>
                    {t("common.save")}
                </Button>
                <Button size="sm" variant="ghost" onClick={onCancel}>
                    {t("common.cancel")}
                </Button>
            </div>
        </form>
    );
}

function IconButton({
    label,
    onClick,
    destructive,
    children,
}: {
    label: string;
    onClick: () => void;
    destructive?: boolean;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className={`flex h-11 w-11 items-center justify-center rounded-lg border border-border-subtle transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                destructive ? "text-status-critical" : "text-content-secondary"
            }`}
        >
            {children}
        </button>
    );
}
